import { useState } from "react";
import { Share2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

export function DishShareDialog({ dish, onClose, lang }) {
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);

  const submit = async () => {
    const to = email.trim().toLowerCase();
    if (!to || !to.includes("@")) {
      toast.error(lang === "es" ? "Email no válido" : "Invalid email");
      return;
    }
    setSending(true);
    try {
      const r = await api.post(`/dishes/${dish.id}/share`, { to_email: to });
      const data = r.data;
      const renamed = data.renamed_to
        ? ` ${lang === "es" ? "(renombrado a" : "(renamed to"} "${data.renamed_to}")`
        : "";
      toast.success(
        lang === "es"
          ? `Plato compartido con ${data.recipient_email}${renamed}`
          : `Dish shared with ${data.recipient_email}${renamed}`
      );
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="dish-share-dialog">
        <DialogHeader>
          <DialogTitle>
            {lang === "es" ? "Compartir plato" : "Share dish"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-[var(--text-secondary)]">
            {lang === "es" ? (
              <>
                Se enviará una <b>copia independiente</b> de <b>{`"${dish.name}"`}</b> al destinatario.
                Los precios actuales de los ingredientes se guardarán como override manual,
                así el coste se mantiene aunque la otra cuenta no tenga las mismas facturas.
              </>
            ) : (
              <>
                An <b>independent copy</b> of <b>{`"${dish.name}"`}</b> will be sent.
                Current ingredient prices are snapshotted as manual overrides so the cost
                stays consistent even if the recipient has no matching invoices yet.
              </>
            )}
          </p>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider">
              {lang === "es" ? "Email del destinatario" : "Recipient email"}
            </Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
              placeholder="user@example.com"
              autoFocus
              data-testid="dish-share-email"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={sending} data-testid="dish-share-cancel">
            {lang === "es" ? "Cancelar" : "Cancel"}
          </Button>
          <Button
            onClick={submit}
            disabled={sending || !email.trim()}
            className="bg-[var(--primary)] hover:bg-[var(--primary-hover)] text-white"
            data-testid="dish-share-submit"
          >
            <Share2 size={14} className="mr-1.5" />
            {sending
              ? (lang === "es" ? "Enviando…" : "Sending…")
              : (lang === "es" ? "Compartir" : "Share")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

